import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Filter,
  BarChart3,
  Clock,
  Users,
  Settings,
  Eye,
  Edit,
  Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';

interface ScheduleEvent {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
  productionJobId: string;
  resourceIds: string[];
  color: string;
}

interface Resource {
  id: string;
  name: string;
  type: 'machine' | 'operator' | 'tool';
  availability: 'available' | 'busy' | 'maintenance';
}

// Mock data
const mockSchedules: ScheduleEvent[] = [
  {
    id: '1',
    title: 'Widget Assembly',
    startDate: '2024-01-15T08:00:00',
    endDate: '2024-01-15T16:00:00',
    status: 'in_progress',
    priority: 'high',
    productionJobId: 'PJ-001',
    resourceIds: ['1', '3'],
    color: 'bg-blue-500',
  },
  {
    id: '2',
    title: 'Component Manufacturing',
    startDate: '2024-01-16T09:00:00',
    endDate: '2024-01-16T17:00:00',
    status: 'scheduled',
    priority: 'medium',
    productionJobId: 'PJ-002',
    resourceIds: ['2', '4'],
    color: 'bg-green-500',
  },
  {
    id: '3',
    title: 'Quality Testing',
    startDate: '2024-01-17T10:00:00',
    endDate: '2024-01-17T14:00:00',
    status: 'scheduled',
    priority: 'critical',
    productionJobId: 'PJ-003',
    resourceIds: ['5'],
    color: 'bg-red-500',
  },
];

const mockResources: Resource[] = [
  { id: '1', name: 'CNC Machine 1', type: 'machine', availability: 'busy' },
  { id: '2', name: 'Assembly Line A', type: 'machine', availability: 'available' },
  { id: '3', name: 'John Smith', type: 'operator', availability: 'busy' },
  { id: '4', name: 'Jane Doe', type: 'operator', availability: 'available' },
  { id: '5', name: 'Quality Station', type: 'tool', availability: 'available' },
];

export default function ScheduleCalendar() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('week');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedResource, setSelectedResource] = useState<string>('all');

  const filteredSchedules = mockSchedules.filter(schedule => {
    const statusMatch = selectedStatus === 'all' || schedule.status === selectedStatus;
    const resourceMatch = selectedResource === 'all' || 
      schedule.resourceIds.includes(selectedResource);
    return statusMatch && resourceMatch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'scheduled': return 'bg-purple-100 text-purple-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    }
    setCurrentDate(newDate);
  };

  const formatDateRange = () => {
    if (viewMode === 'month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else if (viewMode === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return `${startOfWeek.toLocaleDateString()} - ${endOfWeek.toLocaleDateString()}`;
    } else {
      return currentDate.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
  };

  const CalendarView = () => (
    <div className="space-y-4">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigateDate('prev')}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h3 className="text-lg font-semibold">{formatDateRange()}</h3>
          <Button variant="outline" size="sm" onClick={() => navigateDate('next')}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Select value={viewMode} onValueChange={(value: any) => setViewMode(value)}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Day</SelectItem>
              <SelectItem value="week">Week</SelectItem>
              <SelectItem value="month">Month</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="p-2 text-center font-medium text-gray-500 border-b">
            {day}
          </div>
        ))}
        
        {/* Calendar Days */}
        {Array.from({ length: 35 }, (_, i) => {
          const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i - 6);
          const daySchedules = filteredSchedules.filter(schedule => {
            const scheduleDate = new Date(schedule.startDate);
            return scheduleDate.toDateString() === date.toDateString();
          });
          
          return (
            <div key={i} className="min-h-24 p-2 border border-gray-200 hover:bg-gray-50">
              <div className="text-sm font-medium mb-1">
                {date.getDate()}
              </div>
              <div className="space-y-1">
                {daySchedules.map(schedule => (
                  <div
                    key={schedule.id}
                    className={`text-xs p-1 rounded text-white cursor-pointer ${schedule.color}`}
                    onClick={() => navigate(`/manufacturing/schedule/${schedule.id}`)}
                  >
                    {schedule.title}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const GanttView = () => (
    <div className="space-y-4">
      <div className="text-lg font-semibold">Production Timeline</div>
      
      {/* Timeline Header */}
      <div className="grid grid-cols-8 gap-2 text-sm font-medium text-gray-500 border-b pb-2">
        <div>Schedule</div>
        <div>Status</div>
        <div>Priority</div>
        <div>Start</div>
        <div>End</div>
        <div>Duration</div>
        <div>Resources</div>
        <div>Actions</div>
      </div>

      {/* Timeline Items */}
      <div className="space-y-2">
        {filteredSchedules.map(schedule => {
          const startDate = new Date(schedule.startDate);
          const endDate = new Date(schedule.endDate);
          const duration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60));
          
          return (
            <div key={schedule.id} className="grid grid-cols-8 gap-2 items-center p-3 border rounded-lg hover:bg-gray-50">
              <div className="font-medium">{schedule.title}</div>
              <Badge className={getStatusColor(schedule.status)}>
                {schedule.status}
              </Badge>
              <Badge className={getPriorityColor(schedule.priority)}>
                {schedule.priority}
              </Badge>
              <div className="text-sm text-gray-600">
                {startDate.toLocaleDateString()}
                <br />
                {startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className="text-sm text-gray-600">
                {endDate.toLocaleDateString()}
                <br />
                {endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className="text-sm">{duration}h</div>
              <div className="text-sm">
                {schedule.resourceIds.length} resources
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/manufacturing/schedule/${schedule.id}`)}
                >
                  <Eye className="h-3 w-3" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/manufacturing/schedule/${schedule.id}/edit`)}
                >
                  <Edit className="h-3 w-3" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const ResourceView = () => (
    <div className="space-y-4">
      <div className="text-lg font-semibold">Resource Utilization</div>
      
      {mockResources.map(resource => {
        const resourceSchedules = filteredSchedules.filter(schedule =>
          schedule.resourceIds.includes(resource.id)
        );
        
        return (
          <Card key={resource.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {resource.type === 'machine' && <Settings className="h-4 w-4" />}
                  {resource.type === 'operator' && <Users className="h-4 w-4" />}
                  {resource.type === 'tool' && <Settings className="h-4 w-4" />}
                  <CardTitle className="text-base">{resource.name}</CardTitle>
                </div>
                <Badge className={
                  resource.availability === 'available' ? 'bg-green-100 text-green-800' :
                  resource.availability === 'busy' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }>
                  {resource.availability}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {resourceSchedules.length === 0 ? (
                  <p className="text-sm text-gray-500">No scheduled activities</p>
                ) : (
                  resourceSchedules.map(schedule => (
                    <div
                      key={schedule.id}
                      className="flex items-center justify-between p-2 border rounded cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/manufacturing/schedule/${schedule.id}`)}
                    >
                      <div>
                        <div className="font-medium text-sm">{schedule.title}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(schedule.startDate).toLocaleString()} - 
                          {new Date(schedule.endDate).toLocaleString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(schedule.status)}>
                          {schedule.status}
                        </Badge>
                        <Badge className={getPriorityColor(schedule.priority)}>
                          {schedule.priority}
                        </Badge>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Production Schedule Calendar</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate('/manufacturing/schedule/create')}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Schedule
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedResource} onValueChange={setSelectedResource}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by resource" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resources</SelectItem>
                {mockResources.map(resource => (
                  <SelectItem key={resource.id} value={resource.id}>
                    {resource.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              onClick={() => {
                setSelectedStatus('all');
                setSelectedResource('all');
              }}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Schedule Views */}
      <Tabs defaultValue="calendar" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="calendar" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Calendar
          </TabsTrigger>
          <TabsTrigger value="gantt" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Timeline
          </TabsTrigger>
          <TabsTrigger value="resources" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Resources
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calendar">
          <Card>
            <CardContent className="pt-6">
              <CalendarView />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="gantt">
          <Card>
            <CardContent className="pt-6">
              <GanttView />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="resources">
          <div className="space-y-4">
            <ResourceView />
          </div>
        </TabsContent>
      </Tabs>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {filteredSchedules.filter(s => s.status === 'scheduled').length}
              </div>
              <div className="text-sm text-gray-500">Scheduled</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {filteredSchedules.filter(s => s.status === 'in_progress').length}
              </div>
              <div className="text-sm text-gray-500">In Progress</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {filteredSchedules.filter(s => s.status === 'completed').length}
              </div>
              <div className="text-sm text-gray-500">Completed</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {mockResources.filter(r => r.availability === 'available').length}
              </div>
              <div className="text-sm text-gray-500">Available Resources</div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 